<script lang="ts" setup>
import { useRouteParams } from '@vueuse/router'
import LineCheckCircleBroken from '@docue/docue-ui-v2/icons/LineCheckCircleBroken.vue'

definePageMeta({
  middleware: (route) => {
    if (route.query.create === 'true' && route.params.templateId) {
      return navigateTo({
        name: '2-template-create-type-id',
        params: { type: 'template', id: route.params.templateId },
      })
    }
  },
})

const { t } = useI18n()
const { formatCurrency, formatDate } = useIntl()
const { hasValidSubscription } = useSubscription()
const templateId = useRouteParams<string>('templateId')

const isFetchingEnabled = computed(() => !!templateId.value)

const templateDataQuery = useQueryTemplate.byId({
  id: templateId,
  fieldsToInclude: ['categories'],
  useQueryOptions: {
    enabled: isFetchingEnabled,
    placeholderData: (previousData: any) => previousData,
  },
})

watch(templateDataQuery.isError, (isError) => {
  if (isError) {
    navigateTo({
      name: '2-templates-all',
      query: { not_found: 'template' },
      replace: true,
    })
  }
})

const template = computed(() => templateDataQuery.data.value?.data)

const firstFourCategories = computed(() => (template.value?.relationships?.categories?.data ?? []).slice(0, 4))

const categoryLinks = computed(() => firstFourCategories.value.map(({ id, attributes }) => ({
  id,
  text: attributes.name,
  to: {
    name: '2-templates-category-categoryId',
    params: {
      categoryId: id,
    },
  },
})))

const title = computed(() => template.value?.attributes.name)

const isPriceVisible = computed(() => !!template.value?.attributes.unitPrice && !hasValidSubscription.value)

const idOfOpenVideo = ref<string>()
</script>

<template>
  <Head>
    <Title>{{ title }}</Title>
  </Head>

  <TemplateDetail :query="templateDataQuery">
    <template #media>
      <YoutubeVideoList
        v-if="template?.attributes.videoIds?.length"
        :video-ids="template.attributes.videoIds"
        @video-clicked="id => idOfOpenVideo = id"
      />
      <LazyVideoModal
        v-if="idOfOpenVideo"
        :model-value="!!idOfOpenVideo"
        :video-id="idOfOpenVideo"
        @update:model-value="idOfOpenVideo = undefined"
      />
    </template>
    <template #meta>
      <TemplateMeta
        v-if="template"
        :template="template"
      >
        <template #append>
          <div
            v-if="categoryLinks.length"
            class="flex flex-col gap-2"
          >
            <span class="block font-semibold">
              {{ t('template.heading-categories') }}
            </span>
            <ul class="flex flex-wrap gap-2 gap-y-0.5">
              <li
                v-for="({ id, to, text }, index) in categoryLinks"
                :key="id"
              >
                <NuxtLink
                  :to="to"
                  class="hover:underline"
                >
                  {{ text }}
                </NuxtLink>
                <!-- eslint-disable-next-line vue/singleline-html-element-content-newline -->
                <template v-if="index < categoryLinks.length - 1">,</template>
              </li>
            </ul>
          </div>
          <div
            v-if="isPriceVisible"
            class="flex flex-col gap-1.5"
          >
            <span class="block font-semibold">
              {{ formatCurrency(template.attributes.unitPriceWithTax / 100, { maximumFractionDigits: 2 }) }}
            </span>
          </div>
          <div class="flex items-center gap-2">
            <LineCheckCircleBroken class="size-4 text-green-400" />
            <small class="text-gray-400">
              {{ !isTailoredTemplate(template)
                ? t('template.updated-at-by-lawyer', { date: formatDate(template.attributes.updatedAt) })
                : t('template.updated-at', { date: formatDate(template.attributes.updatedAt) })
              }}
            </small>
          </div>
        </template>
      </TemplateMeta>
    </template>
    <template #aside>
      <TemplateRelatedList
        v-if="templateId"
        :template-id="templateId"
      />
    </template>
  </TemplateDetail>
</template>
